import { ReactNode, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';

export type Anchors = 'topCenter' | 'bottomCenter' | 'bestCenter';

const Y_PADDING = 4;

type PopupOverlayProps = {
    map: google.maps.Map | null;
    children: ReactNode;
    position: google.maps.LatLng | google.maps.LatLngLiteral;
    anchor?: Anchors;
    padding?: number;
    // Add a reference to the corresponding marker element
    markerElement?: HTMLElement | null;
};

const PopupOverlay = ({
    map,
    children,
    position,
    anchor = 'topCenter',
    padding = Y_PADDING,
    markerElement = null,
}: PopupOverlayProps) => {
    const root = useRef(document.createElement('div'));
    const overlayRef = useRef<google.maps.OverlayView>();
    const currentAnchor = useRef<Anchors>(anchor);

    const updatePosition = useCallback(() => {
        if (!overlayRef.current || !map) return;
        const overlayProjection = overlayRef.current.getProjection();
        if (!overlayProjection) return;

        const pos = overlayProjection.fromLatLngToDivPixel(
            new google.maps.LatLng(position)
        );
        if (!pos) return;

        let chosenAnchor = anchor;
        if (anchor === 'bestCenter') {
            const mapCenter = overlayProjection.fromLatLngToDivPixel(
                map.getCenter()!
            );
            if (mapCenter && pos.y < mapCenter.y) {
                chosenAnchor = 'bottomCenter';
            } else {
                chosenAnchor = 'topCenter';
            }
        }
        currentAnchor.current = chosenAnchor;

        // Measure the marker element’s dimensions to calculate offset
        let markerHeight = 0;
        if (markerElement) {
            const rect = markerElement.getBoundingClientRect();
            markerHeight = rect.height;
        }

        // Base transformations
        let translateY = 0;
        if (chosenAnchor === 'topCenter') {
            // Position the popup above the marker plus padding
            translateY = -markerHeight / 2 - padding;
        } else {
            // Position the popup below the marker plus padding
            translateY = markerHeight / 2 + padding;
        }

        root.current.style.left = `${pos.x}px`;
        root.current.style.top = `${pos.y}px`;
        root.current.style.transform = `translate(-50%, ${
            chosenAnchor === 'topCenter' ? '-100%' : '0%'
        }) translateY(${translateY}px)`;
    }, [map, position, anchor, padding, markerElement]);

    useEffect(() => {
        if (!map || !window.google) return;

        root.current.style.position = 'absolute';
        root.current.style.willChange = 'transform';

        overlayRef.current = new google.maps.OverlayView();

        overlayRef.current.onAdd = function () {
            const panes = this.getPanes();
            panes?.floatPane.appendChild(root.current);
        };

        overlayRef.current.draw = updatePosition;

        overlayRef.current.onRemove = function () {
            if (root.current.parentElement) {
                root.current.parentElement.removeChild(root.current);
            }
        };

        overlayRef.current.setMap(map);

        const listener = map.addListener('bounds_changed', () => {
            updatePosition();
        });

        return () => {
            overlayRef.current?.setMap(null);
            google.maps.event.removeListener(listener);
        };
    }, [map, updatePosition]);

    return createPortal(children, root.current);
};

export default PopupOverlay;
